import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TrackUseTypeSelect } from 'components/common/TrackUseTypeSelect';
import { InputFormat } from 'components/styleguide';
import { useRootTranslation } from 'utils/hooks';
import { ResolverRow, ResolverOption } from '../reducer';
import LibrarySelect, { PENDING_LIBRARY_OPTION_ID } from './LibrarySelect';
import TrackTitleSelect from './TrackTitleSelect';

const rootT = 'drawers.tracksImportResolver';

export type RowHandlers = {
  onLibrarySelected: (itemId: string, library: ResolverOption) => void;
  onLibraryCreated: (itemId: string, name: string) => void;
  onTrackSelected: (itemId: string, track: ResolverOption) => void;
  onTrackCreated: (itemId: string, title: string) => void;
  onUseChanged: (itemId: string, use: string | null) => void;
  onDurationChanged: (itemId: string, duration: string | null) => void;
  onRowCleared: (itemId: string) => void;
  onAliasRemoved: (itemId: string) => void;
};

type FieldBlockProps = { label: string; children: ReactNode };

const FieldBlock = ({ label, children }: FieldBlockProps) => (
  <Stack gap={0.25}>
    <Typography variant="caption" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    {children}
  </Stack>
);

type QwireTracksFieldsProps = {
  row: ResolverRow;
  handlers: RowHandlers;
  pendingNewLibraries?: string[];
  /* Engine-matched rows shown by the "Show all tracks" toggle — display-only: the BE
     rejects resolutions for items it matched itself (see reducer.ts ResolverRow). */
  readOnly?: boolean;
};

const QwireTracksFields = ({ row, handlers, pendingNewLibraries, readOnly }: QwireTracksFieldsProps) => {
  const t = useRootTranslation(rootT);
  const { itemId, resolution } = row;

  const libraryValue =
    resolution.library ?? (resolution.newLibraryName ? { id: 'new', name: resolution.newLibraryName } : null);
  const titleValue =
    resolution.track ?? (resolution.newTrackTitle ? { id: 'new', name: resolution.newTrackTitle } : null);
  /* A pending "Create New X" library (resolution.library === null, resolution.newLibraryName
     set) can't own any tracks yet -- there's nothing in the catalog under it. Signals
     TrackTitleSelect to skip the catalog search entirely rather than filter its results. */
  const isPendingNewLibrary = !resolution.library && !!resolution.newLibraryName;

  return (
    <Stack gap={1}>
      <FieldBlock label={t('fields.library')}>
        <LibrarySelect
          value={libraryValue}
          placeholder={t('fields.chooseLibrary')}
          onChange={(option) => {
            if (!option) {
              handlers.onRowCleared(itemId);
            } else if (option.id === PENDING_LIBRARY_OPTION_ID) {
              /* Another row's pending new library — it has no real id yet, so this row
                 records the same create-new-library intent instead of a partner pick. */
              handlers.onLibraryCreated(itemId, option.name);
            } else {
              handlers.onLibrarySelected(itemId, option);
            }
          }}
          onCreate={(name) => handlers.onLibraryCreated(itemId, name)}
          onClear={() => handlers.onRowCleared(itemId)}
          pendingNewLibraries={pendingNewLibraries}
          disabled={readOnly}
        />
      </FieldBlock>
      <FieldBlock label={t('fields.trackTitle')}>
        <TrackTitleSelect
          value={titleValue}
          libraryName={resolution.library?.name ?? resolution.newLibraryName ?? null}
          vendorTitle={row.vendorTitle}
          placeholder={t('fields.searchQwireTracks')}
          disabled={readOnly || (!resolution.library && !resolution.newLibraryName)}
          isPendingNewLibrary={isPendingNewLibrary}
          onChange={(option) => (option ? handlers.onTrackSelected(itemId, option) : handlers.onRowCleared(itemId))}
          onCreate={(title) => handlers.onTrackCreated(itemId, title)}
          onClear={() => handlers.onRowCleared(itemId)}
        />
        {resolution.duplicateOfLineNumbers && (
          <Typography variant="caption" sx={{ color: 'error.main' }}>
            {t('fields.duplicateTitle', { rows: resolution.duplicateOfLineNumbers })}
          </Typography>
        )}
      </FieldBlock>
      <Box sx={{ display: 'grid', gridTemplateColumns: '120px 120px', columnGap: 3 }}>
        <FieldBlock label={t('fields.use')}>
          <TrackUseTypeSelect
            usageType="requested"
            value={resolution.use ?? undefined}
            onChange={(next) => handlers.onUseChanged(itemId, next ? next.id : null)}
            testId={`resolver-use-${itemId}`}
            placeholder={t('fields.selectUse')}
            size="small"
            disabled={readOnly}
          />
        </FieldBlock>
        <FieldBlock label={t('fields.duration')}>
          <Box sx={{ '& .MuiInputBase-root': { height: 40 } }}>
            <InputFormat
              fullWidth
              value={resolution.duration ?? ''}
              onChange={(duration: string | undefined) => handlers.onDurationChanged(itemId, duration ?? null)}
              type="time"
              name={`resolver-duration-${itemId}`}
              placeholder={t('fields.durationPlaceholder')}
              disabled={readOnly}
            />
          </Box>
        </FieldBlock>
      </Box>
    </Stack>
  );
};

export default QwireTracksFields;
